import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import {
  HISTORY_RANGES, LOW_PCT, loadBeaconHistory, chartPoints, powerSpans, chartMarkers, drainText, durationText,
  secondsText, tickText, whenText, findTileText, gatewayFindRows, eventLabel, eventTone, formatDays,
} from '../utils/beaconStats';
import { lastSeenLabel } from '../utils/beacons';

// Beacon stats + battery history (jobq-api GET /beacons/stats, GET /beacons/{id}/history, 2026-10-23).
// The fleet strip sits at the top of /beacons; the history panel inside the focused beacon's card.

const TONE_VAR = {
  danger: 'var(--bs-danger)', warning: 'var(--bs-warning)', success: 'var(--bs-success)', secondary: 'var(--bs-secondary-color)',
};

function Tile({ label, main, sub, children, testid }) {
  return (
    <div className="col-6 col-md-4 col-xl" data-testid={testid}>
      <div className="border rounded p-2 h-100" style={{ background: 'var(--bs-body-bg)' }}>
        <div className="small text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.03em' }}>{label}</div>
        <div className="fw-bold" style={{ fontSize: '1.35rem', lineHeight: 1.2 }}>{main}</div>
        {sub && <div className="small text-muted">{sub}</div>}
        {children}
      </div>
    </div>
  );
}

/** The fleet at a glance. `stats` = GET /beacons/stats (null while loading, or when it failed). */
export function FleetStatsStrip({ stats }) {
  const [showGateways, setShowGateways] = useState(false);
  if (!stats) return null;
  const c = stats.counts || {};
  const bat = stats.battery || {};
  const finds = findTileText(stats.finds);
  const gw = stats.gateways || [];
  const online = gw.filter((g) => g.online).length;
  const active = gw.filter((g) => g.is_active).length;
  const act = (stats.activity || []).reduce((a, d) => ({
    finds: a.finds + (d.finds || 0), assigned: a.assigned + (d.assigned || 0), screens: a.screens + (d.screens || 0),
    docks: a.docks + (d.docks || 0),
  }), { finds: 0, assigned: 0, screens: 0, docks: 0 });
  const lowest = (stats.lowest || []).slice(0, 3);
  const since = stats.history && stats.history.since;
  return (
    <div className="mb-3" data-testid="fleet-stats">
      <div className="row g-2">
        <Tile label="Beacons" main={c.total ?? '—'} testid="tile-beacons"
          sub={`${c.on_lots || 0} on pallets · ${c.available || 0} free · ${c.in_range || 0} in range`}>
          {c.quiet > 0 && <div className="small text-danger">{c.quiet} not heard in 24 h</div>}
        </Tile>
        <Tile label="Battery" testid="tile-battery"
          main={<span>{bat.ok || 0}<span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}> ok</span></span>}
          sub={[bat.low ? `${bat.low} low` : null, bat.critical ? `${bat.critical} critical` : null,
            bat.charging ? `${bat.charging} on USB` : null, bat.unknown ? `${bat.unknown} unknown` : null].filter(Boolean).join(' · ') || 'none low'}>
          {lowest.length > 0 && (
            <div className="small mt-1">
              Lowest:{' '}
              {lowest.map((b, i) => (
                <span key={b.beacon_id}>
                  {i > 0 && ', '}
                  <Link to={`/beacons?beacon=${b.beacon_id}`} className={b.battery_state === 'critical' ? 'text-danger' : b.battery_state === 'low' ? 'text-warning' : ''}>
                    {b.beacon_id}
                  </Link>{' '}
                  {Number.isFinite(b.battery_pct) ? `${b.battery_pct}%` : `${(b.battery_mv / 1000).toFixed(2)} V`}
                </span>
              ))}
            </div>
          )}
        </Tile>
        <Tile label={`Find (${stats.days} days)`} main={finds.main} sub={finds.sub} testid="tile-finds">
          {(stats.finds && stats.finds.failed + stats.finds.expired > 0) && (
            <div className="small text-muted">{stats.finds.failed} failed · {stats.finds.expired} timed out</div>
          )}
        </Tile>
        <Tile label="Gateways" main={`${online} / ${active}`} sub="online" testid="tile-gateways">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowGateways((v) => !v)}>
            {showGateways ? 'Hide Find by gateway' : 'Find by gateway'}
          </button>
        </Tile>
        <Tile label={`Activity (${stats.days} days)`} main={act.assigned} testid="tile-activity"
          sub={`pallets tagged · ${act.finds} finds · ${act.docks} dockings · ${act.screens} screen redraws`} />
        <Tile label="Firmware" main={(stats.firmware || [])[0] ? `v${stats.firmware[0].version}` : '—'} testid="tile-firmware"
          sub={(stats.firmware || []).map((f) => `${f.version}: ${f.count}`).join(' · ')} />
      </div>
      {showGateways && (
        <div className="table-responsive mt-2">
          <table className="table table-sm small mb-0" data-testid="gateway-finds">
            <thead><tr><th>Gateway</th><th>Status</th><th className="text-end">Lit it</th><th className="text-end">Failed</th><th className="text-end">Stood down</th><th className="text-end">Success</th><th className="text-end">Median</th></tr></thead>
            <tbody>
              {gatewayFindRows(stats.finds && stats.finds.by_gateway).map((g) => {
                const meta = gw.find((x) => x.gateway_id === g.gateway_id);
                return (
                  <tr key={g.gateway_id}>
                    <td className="font-monospace">{g.gateway_id}</td>
                    <td>{meta ? (meta.online ? <span className="text-success">online</span> : <span className="text-danger">offline · {lastSeenLabel(meta.last_seen_at)}</span>) : '—'}</td>
                    <td className="text-end">{g.ok || 0}</td>
                    <td className="text-end">{(g.failed || 0) + (g.lapsed || 0)}</td>
                    <td className="text-end text-muted">{g.stood_down || 0}</td>
                    <td className="text-end">{g.success_pct == null ? '—' : `${g.success_pct}%`}</td>
                    <td className="text-end">{secondsText(g.median_ok_s)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="small text-muted">"Stood down" = another gateway lit it first.</div>
        </div>
      )}
      {since && (
        <div className="small text-muted mt-1">Battery history kept since {whenText(new Date(since).getTime())}.</div>
      )}
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="border rounded px-2 py-1 small shadow-sm" style={{ background: 'var(--bs-body-bg)', color: 'var(--bs-body-color)' }}>
      <div className="fw-bold">{p.pct}%{Number.isFinite(p.mv) ? ` · ${(p.mv / 1000).toFixed(2)} V` : ''}</div>
      <div className="text-muted">{whenText(p.t)}</div>
      {p.onPower && <div className="text-success">{p.charging ? 'charging' : 'on USB'}{p.docked ? ' · docked' : ''}</div>}
    </div>
  );
}

/** Battery % over time: one line, on-power stretches shaded, events as thin markers, the 20 % line. */
export function BatteryChart({ history, days }) {
  const points = useMemo(() => chartPoints(history && history.samples), [history]);
  const spans = useMemo(() => powerSpans(points, points.length ? points[points.length - 1].t : Date.now()), [points]);
  const markers = useMemo(() => chartMarkers(history && history.events, history && history.chart_event_types, points), [history, points]);
  if (points.length < 2) {
    return (
      <div className="text-muted small border rounded p-3 text-center" data-testid="battery-chart-empty">
        {points.length === 1
          ? `One reading so far (${points[0].pct}% at ${whenText(points[0].t)}). The chart fills in as readings arrive every 15 minutes.`
          : 'No battery readings in this range yet. JobQ stores one every 15 minutes while a gateway hears the beacon.'}
      </div>
    );
  }
  const lo = points[0].t;
  const hi = points[points.length - 1].t;
  return (
    <div data-testid="battery-chart">
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
            <CartesianGrid stroke="var(--bs-border-color)" strokeOpacity={0.5} vertical={false} />
            <XAxis dataKey="t" type="number" scale="time" domain={[lo, hi]} tickFormatter={(v) => tickText(v, days)}
              tick={{ fontSize: 11, fill: 'var(--bs-secondary-color)' }} stroke="var(--bs-border-color)" minTickGap={40} />
            <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 11, fill: 'var(--bs-secondary-color)' }} stroke="var(--bs-border-color)" width={44} />
            {spans.map((s) => (
              <ReferenceArea key={`p${s.x1}`} x1={s.x1} x2={s.x2} fill="var(--bs-success)" fillOpacity={0.12} strokeOpacity={0} ifOverflow="hidden" />
            ))}
            <ReferenceLine y={LOW_PCT} stroke="var(--bs-warning)" strokeDasharray="4 3"
              label={{ value: `${LOW_PCT}% low`, position: 'insideBottomRight', fontSize: 10, fill: 'var(--bs-secondary-color)' }} />
            {markers.map((m, i) => (
              <ReferenceLine key={`m${i}`} x={m.t} stroke={TONE_VAR[m.tone]} strokeOpacity={m.tone === 'secondary' ? 0.45 : 0.85}
                strokeDasharray={m.tone === 'secondary' ? '2 3' : undefined} ifOverflow="hidden" />
            ))}
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'var(--bs-secondary-color)', strokeDasharray: '3 3' }} />
            <Line type="monotone" dataKey="pct" stroke="var(--bs-primary)" strokeWidth={2} dot={false}
              activeDot={{ r: 4, stroke: 'var(--bs-body-bg)', strokeWidth: 2 }} isAnimationActive={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="d-flex flex-wrap gap-3 small text-muted mt-1">
        <span><span style={{ display: 'inline-block', width: 14, height: 2, background: 'var(--bs-primary)', verticalAlign: 'middle' }} /> battery %</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 10, background: 'var(--bs-success)', opacity: 0.25, verticalAlign: 'middle' }} /> on the charger / USB</span>
        {markers.length > 0 && <span>│ events (hover the list below)</span>}
      </div>
    </div>
  );
}

const STAT = ({ label, value, sub, testid }) => (
  <div className="col-6 col-md-3" data-testid={testid}>
    <div className="small text-muted">{label}</div>
    <div className="fw-bold">{value}</div>
    {sub && <div className="small text-muted">{sub}</div>}
  </div>
);

/** One beacon's history: battery chart, lifetime stats, where it has been, who hears it, events. */
export function BeaconHistoryPanel({ beaconId, beacon }) {
  const [days, setDays] = useState(7);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [showEvents, setShowEvents] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setHistory(null);                 // never draw the last beacon's / range's readings under the new one
    loadBeaconHistory(beaconId, days).then((r) => {
      if (cancelled) return;
      if (r.ok && r.body) setHistory(r.body); else { setHistory(null); setError(r.message); }
    });
    return () => { cancelled = true; };
  }, [beaconId, days]);

  const t = (history && history.totals) || {};
  const f = t.finds || {};
  const d = history && history.drain;
  const lastPower = d && d.last_power_at;
  return (
    <div className="mt-3 pt-3 border-top" data-testid="beacon-history">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <strong>Battery &amp; history</strong>
        <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="Range">
          {HISTORY_RANGES.map((r) => (
            <button key={r.days} type="button" className={`btn ${days === r.days ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setDays(r.days)} aria-pressed={days === r.days}>{r.label}</button>
          ))}
        </div>
      </div>
      {error && <div className="small text-danger">{error}</div>}
      {!history && !error && <div className="small text-muted">Loading history…</div>}
      {history && (
        <>
          {history.history && history.history.available === false && (
            <div className="small text-warning mb-2">JobQ isn't storing battery readings here yet.</div>
          )}
          <BatteryChart history={history} days={days} />
          <div className="row g-2 mt-2">
            <STAT label="Battery now" testid="stat-battery"
              value={beacon && Number.isFinite(beacon.battery_pct) ? `${beacon.battery_pct}%` : '—'}
              sub={beacon && Number.isFinite(beacon.battery_mv) ? `${(beacon.battery_mv / 1000).toFixed(2)} V` : null} />
            <STAT label="Battery life" value={drainText(d)} testid="stat-drain"
              sub={d && d.basis_hours ? `from ${d.samples} readings over ${formatDays(d.basis_hours / 24)}` : null} />
            <STAT label="Last on the charger" testid="stat-last-charge"
              value={beacon && (beacon.charging === true || beacon.usb_power === true) ? 'On the charger now'
                : lastPower ? `${durationText(lastPower)} ago` : 'Not in the last 14 days'} />
            <STAT label="In service" testid="stat-in-service"
              value={history.in_service_since ? durationText(history.in_service_since) : '—'}
              sub={history.in_service_since ? `since ${whenText(new Date(history.in_service_since).getTime())}` : null} />
            <STAT label="Pallets carried" value={t.assigned ?? 0} testid="stat-pallets" />
            <STAT label="Finds" testid="stat-finds"
              value={f.requested ? `${f.lit} of ${f.requested} lit` : 'none yet'}
              sub={f.requested ? `${f.success_pct == null ? '—' : `${Math.round(f.success_pct)}%`} · ${secondsText(f.median_s)} median` : null} />
            <STAT label="Screen redraws" value={t.screens ?? 0} testid="stat-screens"
              sub={t.screens_lost ? `${t.screens_lost} lost screen(s)` : 'each costs a little battery'} />
            <STAT label="Dockings" value={t.docks ?? 0} testid="stat-docks"
              sub={t.firmware_updates ? `${t.firmware_updates} firmware update(s)` : null} />
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <div className="small fw-bold mb-1">Where it's been ({HISTORY_RANGES.find((r) => r.days === days).label})</div>
              {history.zones && history.zones.length ? (
                <ul className="list-unstyled small mb-0" data-testid="zone-stays">
                  {history.zones.slice(0, 8).map((z) => (
                    <li key={`${z.label}${z.from}`}>
                      <span className="fw-semibold">{z.label}</span>{' '}
                      <span className="text-muted">
                        {whenText(new Date(z.from).getTime())}
                        {z.readings > 1 ? ` → ${whenText(new Date(z.to).getTime())} (${durationText(z.from, z.to)})` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : <div className="small text-muted">No location scans in this range.</div>}
            </div>
            <div className="col-md-6">
              <div className="small fw-bold mb-1">Heard by</div>
              {history.heard_by && history.heard_by.length ? (
                <ul className="list-unstyled small mb-0" data-testid="heard-by">
                  {history.heard_by.map((g) => (
                    <li key={g.gateway_id}>
                      <span className="font-monospace">{g.gateway_id}</span>{' '}
                      <span className="text-muted">{g.rssi} dBm · {lastSeenLabel(g.heard_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="small text-muted">No gateway signal on record.</div>}
            </div>
          </div>

          <div className="mt-2">
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowEvents((v) => !v)}>
              {showEvents ? 'Hide' : 'Show'} events ({(history.events || []).length})
            </button>
            {showEvents && (
              <ul className="list-unstyled small mb-0 mt-1" style={{ maxHeight: 240, overflowY: 'auto' }} data-testid="beacon-events">
                {(history.events || []).map((e) => (
                  <li key={e.event_id} className="d-flex gap-2">
                    <span className="text-muted text-nowrap">{whenText(new Date(e.created_at).getTime())}</span>
                    <span className={`text-${eventTone(e.event_type) === 'secondary' ? 'body' : eventTone(e.event_type)}`}>{eventLabel(e.event_type)}</span>
                    {e.gateway_id && <span className="text-muted font-monospace">{e.gateway_id}</span>}
                    {e.actor && <span className="text-muted">· {e.actor}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
